import dayjs from 'dayjs';

import { httpDelete, post, put } from 'services/Http';
import {
    applyPreferenceChange,
    EMPTY_PREFERENCES,
    getPreferenceChanges,
    preferenceChangeLabel,
    preferenceChangeRequest,
} from 'services/TherapyGroupPreferenceHelpers';

export const TherapyGroupsAppRoute = '/therapy-groups';
export const TherapyGroupsApiUrl = '/reference/therapy_groups';

export const therapyGroupAppPath = (therapyGroupName) =>
    `${TherapyGroupsAppRoute}/${therapyGroupName}`;

export const therapyGroupUrl = (therapyGroupName) => `${TherapyGroupsApiUrl}/${therapyGroupName}`;

export const therapyGroupActiveLinksUrl = (therapyGroupName) =>
    `${therapyGroupUrl(therapyGroupName)}/active`;

export const therapyGroupLinksUrl = (therapyGroupName) =>
    `${therapyGroupUrl(therapyGroupName)}/links`;

// Link windows use SQL Server BETWEEN, which includes both ends. A null end is open
// for the same 300 years the active-links query uses.
const OPEN_LINK_YEARS = 300;

export const linkInEffect = (link, day) => {
    const asOf = dayjs(day);
    const start = dayjs(link.effective_start);
    const end = link.effective_end ? dayjs(link.effective_end) : start.add(OPEN_LINK_YEARS, 'year');
    return !asOf.isBefore(start) && !asOf.isAfter(end);
};

export const linksInEffect = (links, day) =>
    (links ?? []).filter((link) => linkInEffect(link, day));

export const parentNamesInEffect = (links, day) => [
    ...new Set(linksInEffect(links, day).map((link) => link.parent_product_service)),
];

export const therapyGroupParentProductLinkUrl = (therapyGroupName, parentProductService) =>
    `${therapyGroupUrl(therapyGroupName)}/parent_product_services/${parentProductService}`;

export const parentProductNamesFromSelection = (selected) =>
    selected.map((item) => item?.name ?? item);

export const getTherapyGroupListRows = (therapyGroups) =>
    therapyGroups.map((tg) => ({
        ...tg,
        parentProducts: tg.active_parent_product_services ?? [],
    }));

export const getParentProductLinkChanges = (currentNames, selectedNames) => {
    const current = new Set(currentNames);
    const next = new Set(selectedNames);
    return {
        toAdd: selectedNames.filter((name) => !current.has(name)),
        toRemove: currentNames.filter((name) => !next.has(name)),
    };
};

const parentProductLinkChangeRequests = (therapyGroupName, toAdd, toRemove) => [
    ...toAdd.map((parentProductService) => ({
        kind: 'link',
        parentProductService,
        label: parentProductService,
        request: post(therapyGroupParentProductLinkUrl(therapyGroupName, parentProductService)),
    })),
    ...toRemove.map((parentProductService) => ({
        kind: 'link',
        parentProductService,
        label: parentProductService,
        request: httpDelete(
            therapyGroupParentProductLinkUrl(therapyGroupName, parentProductService),
        ),
    })),
];

const preferenceChangeRequests = (therapyGroupName, changes) =>
    changes.map((change) => ({
        kind: 'preference',
        change,
        label: preferenceChangeLabel(change),
        request: preferenceChangeRequest(therapyGroupName, change),
    }));

const settleChanges = async (changes) => {
    const results = await Promise.allSettled(changes.map((change) => change.request));
    return changes.map((change, index) => ({
        ...change,
        failed: results[index].status === 'rejected',
        reason: results[index].reason,
    }));
};

const apiErrorDetail = (reason) => {
    try {
        const detail = JSON.parse(reason?.message)?.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        return detail?.message;
    } catch {
        return undefined;
    }
};

const formatFailedChange = (change) => {
    const detail = apiErrorDetail(change.reason);
    return detail ? `${change.label} (${detail})` : change.label;
};

const persistedLinkNamesAfter = (currentLinkNames, settled) => {
    const persisted = new Set(currentLinkNames);
    settled
        .filter((change) => change.kind === 'link' && !change.failed)
        .forEach((change) => {
            if (persisted.has(change.parentProductService)) {
                persisted.delete(change.parentProductService);
            } else {
                persisted.add(change.parentProductService);
            }
        });
    return [...persisted];
};

const persistedPreferencesAfter = (initialPreferences, settled) =>
    settled
        .filter((change) => change.kind === 'preference' && !change.failed)
        .reduce(
            (preferences, { change }) => applyPreferenceChange(preferences, change),
            initialPreferences,
        );

const failedChanges = (settled) => settled.filter((change) => change.failed);

const changesDidNotSaveError = (failed, persistedLinkNames, persistedPreferences) => {
    const error = new Error(
        `Therapy group saved, but these changes did not save: ${failed.map(formatFailedChange).join(', ')}. Save again to retry.`,
    );
    error.persistedLinkNames = persistedLinkNames;
    error.persistedPreferences = persistedPreferences;
    return error;
};

export const saveTherapyGroupRecord = async (therapyGroupName, description) => {
    await put(therapyGroupUrl(therapyGroupName), { description: description || null });
};

export const saveParentProductLink = async (therapyGroupName, parentProductService) => {
    await post(therapyGroupParentProductLinkUrl(therapyGroupName, parentProductService));
};

export const removeParentProductLink = async (therapyGroupName, parentProductService) => {
    await httpDelete(therapyGroupParentProductLinkUrl(therapyGroupName, parentProductService));
};

const persistClientPreferences = async (therapyGroupName, initialPreferences, changes) => {
    const settled = await settleChanges(preferenceChangeRequests(therapyGroupName, changes));
    const persistedPreferences = persistedPreferencesAfter(initialPreferences, settled);
    const failed = failedChanges(settled);
    if (failed.length === 0) {
        return persistedPreferences;
    }
    throw changesDidNotSaveError(failed, undefined, persistedPreferences);
};

// Saves changes built elsewhere (the client preference dialog), which can carry window dates
// that a before/after comparison of preferences cannot express.
export const saveClientPreferenceChanges = async (therapyGroupName, initialPreferences, changes) =>
    changes.length === 0
        ? initialPreferences
        : persistClientPreferences(therapyGroupName, initialPreferences, changes);

export const saveClientPreferences = async (therapyGroupName, initialPreferences, preferences) => {
    const changes = getPreferenceChanges(initialPreferences, preferences);
    if (changes.length === 0) {
        return preferences;
    }
    return persistClientPreferences(therapyGroupName, initialPreferences, changes);
};

export const therapyGroupHasPendingChanges = ({
    values,
    initialValues,
    savedLinkNames,
    savedPreferences,
}) => {
    const { toAdd, toRemove } = getParentProductLinkChanges(savedLinkNames, values.parentProducts);
    return (
        values.therapyGroupName !== initialValues.therapyGroupName ||
        values.description !== initialValues.description ||
        toAdd.length > 0 ||
        toRemove.length > 0 ||
        getPreferenceChanges(savedPreferences, values.preferences).length > 0
    );
};

export const saveTherapyGroup = async ({
    therapyGroupName,
    description,
    selectedParentProducts,
    currentLinkNames,
    initialPreferences = EMPTY_PREFERENCES,
    preferences = EMPTY_PREFERENCES,
}) => {
    await saveTherapyGroupRecord(therapyGroupName, description);
    const selectedNames = parentProductNamesFromSelection(selectedParentProducts);
    const { toAdd, toRemove } = getParentProductLinkChanges(currentLinkNames, selectedNames);
    const settled = await settleChanges([
        ...parentProductLinkChangeRequests(therapyGroupName, toAdd, toRemove),
        ...preferenceChangeRequests(
            therapyGroupName,
            getPreferenceChanges(initialPreferences, preferences),
        ),
    ]);
    const failed = failedChanges(settled);
    if (failed.length === 0) {
        return;
    }
    throw changesDidNotSaveError(
        failed,
        persistedLinkNamesAfter(currentLinkNames, settled),
        persistedPreferencesAfter(initialPreferences, settled),
    );
};
